import React, { useCallback, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { basePlugin } from '@atlaskit/editor-plugins/base';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugins/better-type-history';
import { blockTypePlugin } from '@atlaskit/editor-plugins/block-type';
import { clipboardPlugin } from '@atlaskit/editor-plugins/clipboard';
import { compositionPlugin } from '@atlaskit/editor-plugins/composition';
import { focusPlugin } from '@atlaskit/editor-plugins/focus';
import { pastePlugin } from '@atlaskit/editor-plugins/paste';
import { placeholderPlugin } from '@atlaskit/editor-plugins/placeholder';
import { saveOnEnterPlugin } from '@atlaskit/editor-plugins/save-on-enter';
import { typeAheadPlugin } from '@atlaskit/editor-plugins/type-ahead';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import DiagramSymbolActionIcon from '@atlaskit/icon-lab/core/diagram-symbol-action';
import ImageRatioAutoIcon from '@atlaskit/icon-lab/core/image-ratio-auto';
import ImageRatioNarrowIcon from '@atlaskit/icon-lab/core/image-ratio-narrow';
import ImageRatioPortraitIcon from '@atlaskit/icon-lab/core/image-ratio-portrait';
import PaintBrushIcon from '@atlaskit/icon-lab/core/paint-brush';
import ShapeRectangleIcon from '@atlaskit/icon-lab/core/shape-rectangle';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link';
import { RovoIcon } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { IMAGE_GENERATION_THEMES, type ImageGenerationTheme } from '../../types';

export type AIGeneratedMediaState = {
	collection: string | undefined;
	dimensions?: { height: number; width: number };
	fileMimeType?: string;
	id: string | undefined;
};

export type ImageFromAIGenerationProps = {
	/**
	 * Callback to close the popup/container after insertion.
	 */
	closePopup: () => void;
	/**
	 * Callback to insert the generated media into the editor.
	 */
	insertMedia?: (mediaState: AIGeneratedMediaState) => void;
	/**
	 * The media upload collection identifier.
	 * Must have an uploadParams.collection set.
	 */
	mediaUploadCollection: string | undefined;
	/**
	 * Callback invoked when the user submits the prompt.
	 * The caller is responsible for handling the prompt (e.g. opening the
	 * malleable-ai flow). The image generation flow is owned entirely by the caller.
	 *
	 * @param prompt - the text the user typed in the prompt editor.
	 * @param aspectRatio - the aspect ratio string the user picked from the
	 *   ratio dropdown (e.g. "16:9", "1:1", "9:16"). Defaults to "16:9"
	 *   ("Auto 16:9") when the user has not changed the selection. Callers
	 *   that wire this component into the Remix / AIFC flow MUST forward
	 *   this value down to the backend so the generated image respects the
	 *   choice.
	 * @param imageStyle - the image style/theme string the user picked from the
	 *   style dropdown (e.g. "watercolors", "photorealistic"). `null` means the
	 *   user did not pick a specific style. Callers that wire this component into
	 *   the Remix / AIFC flow MUST forward this value down to the backend so the
	 *   generated image respects the choice.
	 */
	onSubmit: (prompt: string, aspectRatio: string | null, imageStyle: string | null) => void;
};

const i18n = defineMessages({
	createImageLabel: {
		id: 'ai-image-generation.image-from-ai-generation.create-image-label',
		defaultMessage: 'Create an image with Rovo',
		description: 'Label text for the Rovo image generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.image-from-ai-generation.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alternative text for the Rovo icon in the AI image generation interface.',
	},
	describeImagePrompt: {
		id: 'ai-image-generation.image-from-ai-generation.describe-image-prompt',
		defaultMessage: 'Describe an image to create',
		description: 'Prompt text for AI image generation',
	},
	describeImagePlaceholder: {
		id: 'ai-image-generation.image-from-ai-generation.describe-image-placeholder',
		defaultMessage: `Describe what you'd like to see...`,
		description: 'Placeholder text for the image generation prompt editor',
	},
	generateImage: {
		id: 'ai-image-generation.image-from-ai-generation.generate-image',
		defaultMessage: 'Generate image',
		description: 'Button text for generating an image through AI',
	},
	pickStyleLabel: {
		id: 'ai-image-generation.image-from-ai-generation.pick-style-label',
		defaultMessage: 'Pick a style',
		description: 'Label for the style/theme picker dropdown',
	},
	styleButtonLabel: {
		id: 'ai-image-generation.image-from-ai-generation.styleButtonLabel',
		defaultMessage: 'Style',
		description: 'Label for the image style picker button when no style has been selected.',
	},
	pickRatioLabel: {
		id: 'ai-image-generation.image-from-ai-generation.pick-ratio-label',
		defaultMessage: 'Ratio',
		description: 'Label for the ratio picker dropdown',
	},
	styleWatercolors: {
		id: 'ai-image-generation.image-from-ai-generation.style-watercolors',
		defaultMessage: 'Watercolors',
		description:
			'Style option in the image generation dropdown that creates watercolor painting-style images with soft, flowing colors.',
	},
	stylePhotoAuto: {
		id: 'ai-image-generation.image-from-ai-generation.stylePhotoAuto',
		defaultMessage: 'Photo (Auto)',
		description:
			'Style option in the image generation dropdown that uses the default photographic image style.',
	},
	stylePhotoButton: {
		id: 'ai-image-generation.image-from-ai-generation.stylePhotoButton',
		defaultMessage: 'Photo',
		description: 'Button label shown when the default photographic image style is selected.',
	},
	styleSketch: {
		id: 'ai-image-generation.image-from-ai-generation.style-sketch',
		defaultMessage: 'Sketch',
		description:
			'Style option in the image generation dropdown that creates sketch-style images with hand-drawn lines and shading.',
	},
	styleThreeD: {
		id: 'ai-image-generation.image-from-ai-generation.style-3d',
		defaultMessage: '3D',
		description:
			'Style option in the image generation dropdown that creates three-dimensional rendered images.',
	},
	aspectRatioLabel: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-label',
		defaultMessage: 'Select a ratio',
		description: 'Label for the aspect ratio picker dropdown in AI image generation.',
	},
	aspectRatioAuto: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-auto-16-9',
		defaultMessage: 'Auto 16:9',
		description: 'Aspect ratio option that uses the default 16:9 ratio determined by the AI model.',
	},
	aspectRatioLandscape: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-landscape',
		defaultMessage: 'Landscape 3:2',
		description: 'Aspect ratio option for landscape images with a 3:2 ratio.',
	},
	aspectRatioSquare: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-square',
		defaultMessage: 'Square 1:1',
		description: 'Aspect ratio option for square images with a 1:1 ratio.',
	},
	aspectRatioPortrait: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-portrait',
		defaultMessage: 'Portrait 2:3',
		description: 'Aspect ratio option for portrait images with a 2:3 ratio.',
	},
	aspectRatioNarrow: {
		id: 'ai-image-generation.image-from-ai-generation.aspect-ratio-narrow',
		defaultMessage: 'Narrow 9:16',
		description: 'Aspect ratio option for narrow images with a 9:16 ratio.',
	},
	footerMessage: {
		id: 'ai-image-generation.image-from-ai-generation.footer-message',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Footer disclaimer message informing users that results are AI-generated',
	},
});

const styles = cssMap({
	rovoIcon: {
		width: '16px',
		height: '16px',
		marginLeft: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.150'),
		marginBlockStart: token('space.150'),
		//* Light/elevation.shadow.aiinput */
		// @ts-expect-error - raw boxShadow / ProseMirror nested selector not supported in cssMap strict types; required by editor integration
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 -2px 50px 8px rgba(30, 31, 33, 0.08)',
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
	actionsRow: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.025'),
	},
	styleButtonContainer: {
		marginInlineStart: token('space.negative.100'),
	},
	submitButtonContainer: {
		marginInlineStart: 'auto',
	},
	footer: {
		color: token('color.text.subtlest'),
		paddingBlockStart: token('space.100'),
		display: 'flex',
		justifyContent: 'center',
		marginBlockStart: token('space.negative.075'),
		marginBlockEnd: token('space.negative.150'),
	},
});

export function ImageFromAIGeneration({
	mediaUploadCollection,
	closePopup,
	onSubmit,
	insertMedia,
}: ImageFromAIGenerationProps): React.JSX.Element {
	const intl = useIntl();

	const [selectedTheme, setSelectedTheme] = useState<{
		buttonLabel: string;
		label: string;
		value: ImageGenerationTheme | null;
	} | null>(null);

	const [selectedAspectRatio, setSelectedAspectRatio] = useState<{
		label: string;
		value: string | null;
	} | null>(null);

	const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

	const themeOptions = [
		{
			buttonLabel: intl.formatMessage(i18n.stylePhotoButton),
			label: intl.formatMessage(i18n.stylePhotoAuto),
			value: null,
		},
		{
			buttonLabel: intl.formatMessage(i18n.styleThreeD),
			label: intl.formatMessage(i18n.styleThreeD),
			value: IMAGE_GENERATION_THEMES.threeD,
		},
		{
			buttonLabel: intl.formatMessage(i18n.styleSketch),
			label: intl.formatMessage(i18n.styleSketch),
			value: IMAGE_GENERATION_THEMES.sketch,
		},
		{
			buttonLabel: intl.formatMessage(i18n.styleWatercolors),
			label: intl.formatMessage(i18n.styleWatercolors),
			value: IMAGE_GENERATION_THEMES.watercolors,
		},
	];

	const aspectRatioOptions: Array<{
		icon: typeof ImageRatioAutoIcon;
		label: string;
		value: string | null;
	}> = [
		{
			label: intl.formatMessage(i18n.aspectRatioAuto),
			value: '16:9',
			icon: ImageRatioAutoIcon,
		},
		{
			label: intl.formatMessage(i18n.aspectRatioLandscape),
			value: '3:2',
			icon: DiagramSymbolActionIcon,
		},
		{
			label: intl.formatMessage(i18n.aspectRatioSquare),
			value: '1:1',
			icon: ShapeRectangleIcon,
		},
		{
			label: intl.formatMessage(i18n.aspectRatioPortrait),
			value: '2:3',
			icon: ImageRatioPortraitIcon,
		},
		{
			label: intl.formatMessage(i18n.aspectRatioNarrow),
			value: '9:16',
			icon: ImageRatioNarrowIcon,
		},
	];

	const selectedAspectRatioOption = aspectRatioOptions.find(
		(option) => option.value === selectedAspectRatio?.value,
	);
	const SelectedAspectRatioIcon = selectedAspectRatioOption?.icon ?? ImageRatioAutoIcon;
	const aspectRatioButtonLabel =
		selectedAspectRatio?.value ?? intl.formatMessage(i18n.pickRatioLabel);
	const styleButtonLabel = selectedTheme?.buttonLabel ?? intl.formatMessage(i18n.styleButtonLabel);

	const textRef = useRef<string>('');
	const textSerializerRef = useRef<ReturnType<typeof TextSerializer.fromSchema> | null>(null);

	const handleSubmit = useCallback(
		(prompt: string) => {
			if (!prompt.trim()) {
				return;
			}
			const aspectRatioToSend = selectedAspectRatio?.value ?? null;
			const imageStyleToSend = selectedTheme?.value ?? null;
			onSubmit(prompt, aspectRatioToSend, imageStyleToSend);
		},
		[onSubmit, selectedAspectRatio, selectedTheme],
	);

	const handleSubmitRef = useRef(handleSubmit);
	useEffect(() => {
		handleSubmitRef.current = handleSubmit;
	}, [handleSubmit]);

	const getTextSerializer = useCallback((editorView: EditorView) => {
		if (!textSerializerRef.current) {
			textSerializerRef.current = TextSerializer.fromSchema(editorView.state.schema);
		}
		return textSerializerRef.current;
	}, []);

	const onSave = useCallback(
		(editorView: EditorView) => {
			const text = getTextSerializer(editorView).serializeFragment(editorView.state.doc.content);
			handleSubmitRef.current(text);
		},
		[getTextSerializer],
	);

	const handleEditorChange = useCallback(
		(editorView: EditorView) => {
			const text = getTextSerializer(editorView).serializeFragment(editorView.state.doc.content);
			textRef.current = text;
			setIsSubmitDisabled(!text.trim());
		},
		[getTextSerializer],
	);

	const handleSubmitClick = useCallback(() => {
		handleSubmit(textRef.current);
	}, [handleSubmit]);

	const { preset } = usePreset(
		(builder) =>
			builder
				.add([basePlugin, {}])
				.add(compositionPlugin)
				.add(focusPlugin)
				.add(betterTypeHistoryPlugin)
				.add([pastePlugin, {}])
				.add(clipboardPlugin)
				.add(typeAheadPlugin)
				.add([
					placeholderPlugin,
					{ placeholder: intl.formatMessage(i18n.describeImagePlaceholder) },
				])
				.add([blockTypePlugin, { allowBlockType: { exclude: ['heading', 'blockquote'] } }])
				.add([saveOnEnterPlugin, onSave]),
		[onSave, intl],
	);

	return (
		<EditorContext>
			<Stack grow="fill">
				<Box>
					<Inline space="space.100" alignBlock="center">
						<Box xcss={styles.rovoIcon}>
							<RovoIcon
								appearance="brand"
								size="xsmall"
								label={intl.formatMessage(i18n.rovoIconAlt)}
							/>
						</Box>
						<Text size="medium" weight="bold">
							{intl.formatMessage(i18n.createImageLabel)}
						</Text>
					</Inline>
				</Box>

				<Box xcss={styles.imagePromptContainer}>
					<Box xcss={styles.stack}>
						<ComposableEditor
							appearance="chromeless"
							preset={preset}
							shouldFocus
							onChange={handleEditorChange}
							assistiveLabel={intl.formatMessage(i18n.describeImagePlaceholder)}
						/>
						<Box xcss={styles.actionsRow}>
							{expValEquals(
								'editor-image-generation-styles-and-aspect-ratios',
								'isEnabled',
								true,
							) && (
								<Box xcss={styles.styleButtonContainer}>
									<DropdownMenu<HTMLButtonElement>
										placement="bottom-start"
										shouldRenderToParent
										strategy="absolute"
										zIndex={9999}
										trigger={({ triggerRef, ...triggerProps }) => (
											<Button
												ref={triggerRef}
												{...triggerProps}
												appearance="subtle"
												spacing="compact"
												aria-label={intl.formatMessage(i18n.pickStyleLabel)}
												iconBefore={PaintBrushIcon}
												testId="image-generation-style-trigger"
											>
												{styleButtonLabel}
											</Button>
										)}
									>
										<DropdownItemGroup>
											{themeOptions.map((option) => (
												<DropdownItem
													key={option.value}
													isSelected={selectedTheme?.value === option.value}
													onClick={() =>
														setSelectedTheme((currentTheme) =>
															currentTheme?.value === option.value ? null : option,
														)
													}
												>
													{option.label}
												</DropdownItem>
											))}
										</DropdownItemGroup>
									</DropdownMenu>
								</Box>
							)}
							{expValEquals(
								'editor-image-generation-styles-and-aspect-ratios',
								'isEnabled',
								true,
							) && (
								<DropdownMenu<HTMLButtonElement>
									placement="bottom-start"
									shouldRenderToParent
									strategy="absolute"
									zIndex={9999}
									trigger={({ triggerRef, ...triggerProps }) => (
										<Button
											ref={triggerRef}
											{...triggerProps}
											appearance="subtle"
											spacing="compact"
											aria-label={intl.formatMessage(i18n.aspectRatioLabel)}
											iconBefore={SelectedAspectRatioIcon}
											testId="image-generation-aspect-ratio-trigger"
										>
											{aspectRatioButtonLabel}
										</Button>
									)}
								>
									<DropdownItemGroup>
										{aspectRatioOptions.map((option) => {
											const OptionIcon = option.icon;

											return (
												<DropdownItem
													key={option.value}
													elemBefore={<OptionIcon label="" size="small" />}
													isSelected={selectedAspectRatio?.value === option.value}
													onClick={() =>
														setSelectedAspectRatio((currentAspectRatio) =>
															currentAspectRatio?.value === option.value
																? null
																: {
																		label: option.label,
																		value: option.value,
																	},
														)
													}
												>
													{option.label}
												</DropdownItem>
											);
										})}
									</DropdownItemGroup>
								</DropdownMenu>
							)}
							<Box xcss={styles.submitButtonContainer}>
								<IconButton
									testId="image-generation-submit"
									appearance="primary"
									icon={ArrowUpIcon}
									label={intl.formatMessage(i18n.generateImage)}
									isDisabled={isSubmitDisabled}
									onClick={handleSubmitClick}
								/>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box xcss={styles.footer}>
					<Inline space="space.050" alignBlock="center">
						<InformationCircleIcon color="currentColor" label="" size="small" />
						<Link
							appearance="subtle"
							href="https://www.atlassian.com/trust/atlassian-intelligence"
							target="_blank"
						>
							<Text size="small" color="color.text.subtlest">
								{intl.formatMessage(i18n.footerMessage)}
							</Text>
						</Link>
					</Inline>
				</Box>
			</Stack>
		</EditorContext>
	);
}
